import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useSearch, useLocation } from 'wouter'
import queryString from 'query-string'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'

import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  CollapsibleTriangleIcon,
} from '@/components/ui/collapsible'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { BookIcon } from '@/components/icons/BookIcon'
import { DocumentTextIcon } from '@/components/icons/DocumentTextIcon'
import { ListBulletIcon } from '@/components/icons/ListBulletIcon'
import { ClockIcon } from '@/components/icons/ClockIcon'
import { PartIcon } from '@/components/icons/PartIcon'
import {
  usePartsTables,
  useToolsTables,
  useUpdateTable,
} from '@/services/hooks/template_attributes'
import {
  useUpdateDocumentPage,
  useToggleSubpageVisibility,
} from '@/services/queries/document_pages'
import { useStepBOM } from '../OperationDocument/hooks/useBOM'
import { ToolIcon } from '@/components/icons/ToolIcon'
import { DocumentPage, EnrichedDocumentPage } from '@/lib/api/client'
import { useDocumentState } from '@/state'
import { ProjectDocumentTitle } from '@/components/core/ProjectDocumentTitle'
import { useCadPageParams } from '@/pages/CADPage/hooks'
import { cn, isEmptyOrSpaces } from '@/utils'
import { EditableField } from '@/components/core/EditableField'
import {
  useAssemblyTreeQuery,
  useUpdateCadVersionMutation,
} from '@/pages/CADPage/queries'
import { useCADQuery } from '@/services/queries/cads'
import { useTourProgressStore } from '@/components/core/AppTour/state'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { SectionsIcon } from '@/components/icons/SectionsIcon'
import { LETTER_PAGE_HEIGHT } from '../DocumentTemplate'

export const WorkInstructionsTOC = () => {
  const { isLoading, data } = useDocumentPageQuery()

  const { data: readOnlyData } = useIsReadOnly()
  const isReadOnly = readOnlyData.isReadOnly

  const currentPageInView = useDocumentState(
    useShallow((state) => state.currentVirtualPageInView),
  )
  const scrollToPage = useDocumentState((state) => state.scrollToPageId)
  const project = useMemo(() => data?.project, [data?.project])
  const allOperations = useMemo(
    () => data?.documentPages || [],
    [data?.documentPages],
  )
  const numOfDocVersions = useMemo(() => {
    const versionIndex =
      data.documentVersions?.findIndex(
        (d) => d.version_number === data.documentVersion?.version_number,
      ) ?? 0

    const versions = data.documentVersions?.slice(versionIndex)

    return versions?.length || 0
  }, [data.documentVersion?.version_number, data.documentVersions])
  const enrichOperations = useCallback(
    (operation: DocumentPage, parentOperationOrderNumber?: string | number) => {
      const childOperations = allOperations.filter(
        (op) => op.parent === operation.id,
      )

      const orderNumber = parentOperationOrderNumber
        ? `${parentOperationOrderNumber}.${operation.order_number}`
        : operation.order_number

      return {
        ...operation,
        order_number: orderNumber,
        children: childOperations.map((op) =>
          enrichOperations(op, orderNumber),
        ),
      }
    },
    [allOperations],
  )
  const rootOperations = useMemo(
    () =>
      allOperations
        .filter((op) => !op.parent)
        .map((op) => enrichOperations(op)),
    [allOperations, enrichOperations],
  )

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!project) {
    return null
  }

  const navigateToPage = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    pageId: string,
  ) => {
    e.preventDefault()
    scrollToPage(pageId, {
      align: 'start',
      behavior: 'smooth',
    })
  }

  return (
    <div className="h-full overflow-auto pb-5 text-sm">
      <div className="my-4 px-10">
        <ProjectDocumentTitle isReadOnly={isReadOnly} />
      </div>
      <div data-testid="toc-container" className="flex flex-col">
        <a
          className={cn(
            'flex items-center space-x-2 px-10 py-1.5 font-medium hover:bg-indigo-50 hover:text-primary-50 cursor-pointer',
            {
              'bg-indigo-100': currentPageInView === 'cover',
            },
          )}
          onClick={(e) => navigateToPage(e, 'cover')}
        >
          <BookIcon className="w-4 h-4" />
          <span>Cover</span>
        </a>
        {numOfDocVersions > 1 && (
          <a
            className={cn(
              'flex items-center space-x-2 px-10 py-1.5 font-medium hover:bg-indigo-50 hover:text-primary-50 cursor-pointer',
              {
                'bg-indigo-100': currentPageInView === 'history',
              },
            )}
            onClick={(e) => navigateToPage(e, 'history-0')}
          >
            <ClockIcon className="w-4 h-4" />
            <span>Version History</span>
          </a>
        )}

        <a
          className={cn(
            'flex items-center space-x-2 px-10 py-1.5 font-medium hover:bg-indigo-50 hover:text-primary-50 cursor-pointer',
            {
              'bg-indigo-100': /^(?:bom-|tools_bom-)\d+$/.test(
                currentPageInView,
              ),
            },
          )}
          onClick={(e) => navigateToPage(e, 'bom-0')}
        >
          <ListBulletIcon className="w-4 h-4" />
          <span>Bill of Materials</span>
        </a>
        {rootOperations.map((operation, i) => {
          return (
            <OperationItem
              operation={operation}
              key={`operation_${i}`}
              navigateToPage={navigateToPage}
              isReadOnly={isReadOnly}
              index={String(i + 1)}
            />
          )
        })}
      </div>
    </div>
  )
}

const OperationItem = ({
  operation,
  navigateToPage,
  isReadOnly,
  index,
}: {
  operation: EnrichedDocumentPage
  navigateToPage: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    pageId: string,
  ) => void
  isReadOnly: boolean
  index: string
}) => {
  const currentPageInView = useDocumentState(
    useShallow((state) => state.currentVirtualPageInView),
  )
  const { mutate: updateOperation } = useUpdateDocumentPage()
  const { data: cadData } = useCADQuery()
  const documentVersionId = cadData?.documentVersion?.id as string
  const isSubOperation = !!operation.parent

  const [, setLocation] = useLocation()
  const search = useSearch()
  const { projectId, documentId } = useCadPageParams()
  const { markStepCompleted } = useTourProgressStore()

  const operationName = isEmptyOrSpaces(operation.name)
    ? isSubOperation
      ? 'Suboperation'
      : 'Operation'
    : operation.name

  const _subPages =
    (operation.template_values?.subPages as string[] | undefined) ?? []
  const _hiddenSubPageIds = new Set<string>(
    (operation.template_values?.hiddenSubPages as string[] | undefined) ?? [],
  )
  // When no real subpages exist, the page itself acts as the single section
  const effectiveSections: string[] =
    _subPages.length > 0 ? _subPages : [operation.id as string]
  const allSectionsHidden =
    effectiveSections.length > 0 &&
    effectiveSections.every((id) => _hiddenSubPageIds.has(id))
  const isHidden = allSectionsHidden

  return (
    <Collapsible>
      <div className="group relative">
        <a
          className={cn(
            'operation-item flex items-center space-x-2 font-medium hover:bg-indigo-50 hover:text-primary-50 pr-1 cursor-pointer',
            {
              'bg-indigo-100': currentPageInView === `${operation.id}`,
              'opacity-50 text-gray-400': isHidden,
            },
          )}
          data-testid={`toc-operation-${index}`}
          onClick={(e) => {
            e.preventDefault()
            const searchParams = queryString.parse(search) as {
              cv?: string // cad version
              dv?: string // document version
              page_id?: string // document page id
            }
            const route = `~/p/${projectId}/document/${documentId}/edit?${queryString.stringify({ ...searchParams, page_id: operation.id })}`
            setLocation(route)

            setTimeout(() => {
              const pageId = operation.id
              navigateToPage(e, pageId as string)

              markStepCompleted(6)
            })
          }}
        >
          <CollapsibleTrigger>
            <Button variant="ghost" className="px-2">
              <CollapsibleTriangleIcon className="w-5 h-5 text-indigo-400" />
            </Button>
          </CollapsibleTrigger>
          <span className="font-bold mr-1">{index}</span>
          <DocumentTextIcon className="w-4 h-4 !ml-2 stroke-gray-800" />

          {isReadOnly ? (
            <div className="flex-1 truncate">{operationName}</div>
          ) : (
            <span className="flex items-center gap-1 min-w-0 flex-1">
              <EditableField
                dataTestId={`operation-${operation.order_number}`}
                value={operationName}
                onSaveHandler={(name: string) => {
                  updateOperation({
                    documentPageId: operation.id as string,
                    values: {
                      name,
                      template_values: operation.template_values,
                      parent_id: operation.parent as string,
                    },
                  })
                }}
              />
            </span>
          )}

          {!isReadOnly && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto p-1 rounded hover:bg-indigo-100 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      updateOperation({
                        documentPageId: operation.id as string,
                        values: {
                          name: operation.name,
                          template_values: {
                            ...operation.template_values,
                            hiddenSubPages: allSectionsHidden
                              ? []
                              : effectiveSections,
                          },
                          parent_id: operation.parent as string,
                        },
                      })
                    }}
                  >
                    {isHidden ? (
                      <EyeIcon className="w-3.5 h-3.5 text-gray-400" />
                    ) : (
                      <EyeSlashIcon className="w-3.5 h-3.5 text-gray-400" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="bg-black text-white">
                  {allSectionsHidden
                    ? 'Show all sections'
                    : 'Hide all sections'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </a>
      </div>
      <CollapsibleContent className="ml-10">
        <PartsList documentPageId={operation.id} isReadOnly={isReadOnly} />
        <ToolsList documentPageId={operation.id as string} />
        <SectionsList
          operation={operation}
          isReadOnly={isReadOnly}
          documentVersionId={documentVersionId}
        />
      </CollapsibleContent>
      {!!operation.children?.length && (
        <div className="pl-2 ml-2">
          {operation.children.map((child, i) => (
            <OperationItem
              key={child.id}
              operation={child}
              index={`${index}.${i + 1}`}
              navigateToPage={navigateToPage}
              isReadOnly={isReadOnly}
            />
          ))}
        </div>
      )}
    </Collapsible>
  )
}

const SectionsList = ({
  operation,
  isReadOnly,
  documentVersionId,
}: {
  operation: EnrichedDocumentPage
  isReadOnly: boolean
  documentVersionId: string
}) => {
  const { mutate: toggleSubpageVisibility } = useToggleSubpageVisibility()
  const scrollToPage = useDocumentState((state) => state.scrollToPageId)
  const zoom = useDocumentState((state) => state.zoom)
  const subPages: string[] =
    (operation.template_values?.subPages as string[] | undefined) ?? []
  const hiddenSubPageIds = new Set<string>(
    (operation.template_values?.hiddenSubPages as string[] | undefined) ?? [],
  )
  // Visible subpages only — matches the rendered order used for position calc
  const visibleSubPages = subPages.filter((id) => !hiddenSubPageIds.has(id))

  // Only show Sections group when there is more than one subpage
  if (subPages.length <= 1) return null

  const handleSectionClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    subPageId: string,
  ) => {
    e.preventDefault()
    const subPageIndex = visibleSubPages.indexOf(subPageId)
    const offset =
      subPageIndex > 0 ? subPageIndex * LETTER_PAGE_HEIGHT * (zoom / 100) : 0
    scrollToPage(operation.id as string, {
      align: 'start',
      offset,
    })
  }

  return (
    <div className="pl-2 mt-2">
      <div className="flex items-center space-x-2">
        <SectionsIcon className="w-5 h-5 stroke-gray-800" />
        <span className="font-semibold">Sections</span>
      </div>
      <div className="pl-6 mt-2">
        {subPages.map((subPageId, i) => {
          const isSectionHidden = hiddenSubPageIds.has(subPageId)
          return (
            <div
              key={subPageId}
              className="group relative flex items-center pr-2 my-1"
            >
              <a
                className={cn('flex-1 cursor-pointer hover:text-primary-50', {
                  'opacity-50 text-gray-400': isSectionHidden,
                })}
                onClick={(e) => handleSectionClick(e, subPageId)}
              >
                Section {i + 1}
              </a>

              {!isReadOnly && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-indigo-100 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (documentVersionId) {
                            toggleSubpageVisibility({
                              parentPageId: operation.id as string,
                              subPageId,
                              documentVersionId,
                              parentPage: operation,
                            })
                          }
                        }}
                      >
                        {isSectionHidden ? (
                          <EyeIcon className="w-3.5 h-3.5 text-gray-400" />
                        ) : (
                          <EyeSlashIcon className="w-3.5 h-3.5 text-gray-400" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-black text-white">
                      {isSectionHidden ? 'Show section' : 'Hide section'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const PartsList = ({
  documentPageId,
  isReadOnly,
}: {
  documentPageId?: string | null
  isReadOnly: boolean
}) => {
  const bom = useStepBOM({ documentPageId })

  const {
    data: { assemblyTree },
  } = useAssemblyTreeQuery()
  const { data: cadData, refetchCadVersion } = useCADQuery()
  const { mutate: updateCadVersion } = useUpdateCadVersionMutation()
  const { mutateAsync: updateTable } = useUpdateTable()

  const cadVersionId = useMemo(
    () => cadData?.version?.id as string,
    [cadData?.version],
  )
  const documentVersionId = useMemo(
    () => cadData?.documentVersion?.id as string,
    [cadData?.documentVersion],
  )

  const partsTableTemplates = usePartsTables(documentPageId)
  const partsCustomRows = useMemo(() => {
    return partsTableTemplates.reduce((accum, template) => {
      const rows = template.template_values.rows as any
      const columns = template.template_values.columns as any

      if (!rows?.length) return [...accum]

      if (columns?.length) {
        return [...accum, ...(rows || [].slice(bom.length, rows.length))]
      }

      const mappedRows = rows.map((row) => ({
        ...row,
        tableId: template.id,
      }))

      return [...accum, ...mappedRows]
    }, [] as any)
  }, [bom?.length, partsTableTemplates])

  const customParts = useMemo(() => {
    return partsCustomRows.reduce((accum, row) => {
      const foundRow = accum.find((r) => r.partName === row.partName)
      if (foundRow) {
        foundRow.quantity += Number(row.quantity)
        foundRow.tableIds.push(row.tableId)
      } else {
        accum.push({
          partName: row.partName,
          quantity: Number(row.quantity),
          tableIds: [row.tableId],
        })
      }
      return accum
    }, [])
  }, [partsCustomRows])

  const onUpdatePartName = useCallback(
    async ({
      uuids,
      updatedPartName,
    }: {
      uuids: string[]
      updatedPartName: string
    }) => {
      uuids.forEach((uuid) => {
        const node = assemblyTree?.nodes?.find((node) => node.uuid === uuid)
        if (node) {
          node.display_name = updatedPartName
        }
      })

      if (assemblyTree && cadVersionId && documentVersionId) {
        updateCadVersion({
          cadVersionId,
          documentVersionId,
          assemblyTree,
        })

        await refetchCadVersion()
      }
    },
    [
      assemblyTree,
      cadVersionId,
      documentVersionId,
      refetchCadVersion,
      updateCadVersion,
    ],
  )

  const onUpdateCustomPartName = useCallback(
    async ({ updatedPartIndex, updatedPartName }) => {
      const partIndex = updatedPartIndex - bom.length
      const updatedPart = customParts.find((_, i) => i === partIndex)

      if (updatedPart) {
        const tableUuids = updatedPart.tableIds
        const oldPartName = updatedPart.partName

        if (tableUuids && tableUuids.length) {
          const relevantTables = partsTableTemplates.filter((table) =>
            tableUuids.includes(table.id),
          )

          relevantTables.forEach(async (table) => {
            const rows = table.template_values.rows as any
            const updatedRows = rows.map((row: any) => {
              if (row.partName === oldPartName) {
                return {
                  ...row,
                  partName: updatedPartName,
                }
              }
              return row
            })

            const tableData = {
              ...table.template_values,
              rows: updatedRows,
            }

            if (table.id && documentPageId) {
              await updateTable({
                templateAttributeId: table.id,
                documentPageId,
                tableData,
              })
            }
          })
        }
      }
    },
    [bom.length, customParts, documentPageId, partsTableTemplates, updateTable],
  )

  const handlePartRename = useCallback(
    async (updatedPartName: string, partData: any) => {
      const uuids = partData.UUIDs
      if (uuids) {
        await onUpdatePartName({
          uuids,
          updatedPartName,
        })
      } else {
        await onUpdateCustomPartName({
          updatedPartIndex: partData.index,
          updatedPartName,
        })
      }
    },
    [onUpdateCustomPartName, onUpdatePartName],
  )

  return (
    <div className="pl-2 pt-2">
      <div className="flex items-center space-x-2">
        <PartIcon className="w-4 h-4" />
        <span className="font-semibold">Parts</span>
      </div>
      <div className="pl-6 mt-2">
        {[...bom, ...customParts].map((part, i) => (
          <div
            key={`part_${i}`}
            className="flex items-center space-x-2 pr-2 my-1"
          >
            {isReadOnly ? (
              part.partName
            ) : (
              <EditableField
                value={part.partName as string}
                onSaveHandler={(partName) =>
                  handlePartRename(partName, {
                    ...part,
                    index: i,
                  })
                }
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const ToolsList = ({ documentPageId }: { documentPageId: string }) => {
  const toolsTableTemplates = useToolsTables(documentPageId)
  const toolsRows = useMemo(() => {
    return toolsTableTemplates.reduce((accum, template) => {
      const rows = template.template_values.rows as any

      if (!rows?.length) return [...accum]

      return [...accum, ...rows]
    }, [] as any)
  }, [toolsTableTemplates])

  const toolsList = useMemo(
    () =>
      Array.from(
        new Set(toolsRows.map((toolsRow) => toolsRow.toolName || '')),
      ).map((toolName) => ({ toolName })),
    [toolsRows],
  )

  return toolsList.length ? (
    <div className="pl-2 mt-2">
      <div className="flex items-center space-x-2">
        <ToolIcon className="w-4 h-4" />
        <span className="font-semibold">Tools</span>
      </div>
      <div className="pl-6 mt-2">
        {toolsList.map((tool, i) => (
          <div key={`tool${i}`} className="flex items-center space-x-2">
            <span>{tool.toolName as any}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null
}
